import { flattenSections } from './numbering.js';
import type {
  CrossReferenceAttrs,
  DocumentContent,
  DocumentFrontMatter,
  Mark,
  RichNode,
} from './types.js';

export interface MarkdownOptions {
  /** Emit YAML front matter (default true when metadata is supplied). */
  frontMatter?: DocumentFrontMatter | null;
  /** Emit the document title as an H1 above the sections (default true). */
  includeTitle?: boolean;
  title?: string;
  /** Prefix headings with their clause number (default true). */
  numberHeadings?: boolean;
  /** Emit a table of contents after the title (default false). */
  includeToc?: boolean;
  /** Omit sections flagged `internal` (default true). */
  skipInternal?: boolean;
  /** Base heading level for the top-level sections (default 2, under the H1). */
  baseHeadingLevel?: number;
  /**
   * How to render cross-document references. `link` (default) produces a
   * relative Markdown link to `<REF>.md`; `text` produces plain text so the
   * Markdown stays valid when exported on its own.
   */
  crossReferenceStyle?: 'link' | 'text';
}

/**
 * Accepts only link targets that are safe to write into an exported document.
 * Mirrors `safeHref` in the HTML renderer, including the refusal of
 * protocol-relative URLs.
 */
function safeMarkdownHref(href: string): string {
  const trimmed = href.replace(/[\u0000-\u001f\u007f]/g, '').trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('//') || trimmed.startsWith('\\')) return '';
  if (/^(https?:|mailto:)/i.test(trimmed)) return trimmed;
  if (/^(#|\/|\.\/|\.\.\/)/.test(trimmed)) return trimmed;
  if (/^[\w.-]+\.md(#.*)?$/i.test(trimmed)) return trimmed;
  return '';
}

/**
 * Escapes what is ambiguous inline, in a single pass.
 *
 * A single pass matters: escaping character classes one after another would
 * re-escape the backslashes inserted by an earlier pass, so `__init__` would
 * come out as `\\__init\\_\\_`. Characters that are only special at the start of a
 * line are handled separately by `escapeLineStarts`.
 */
function escapeText(text: string): string {
  let out = '';
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i]!;
    const previous = i > 0 ? text[i - 1]! : '';
    const next = i + 1 < text.length ? text[i + 1]! : '';

    switch (char) {
      case '\\':
      case '`':
      case '*':
      case '[':
      case ']':
      case '<':
        out += `\\${char}`;
        break;
      case '_': {
        // Intraword underscores are not emphasis in GFM, so `snake_case` is safe.
        const wordBefore = /[\p{L}\p{N}]/u.test(previous);
        const wordAfter = /[\p{L}\p{N}]/u.test(next);
        out += wordBefore && wordAfter ? '_' : '\\_';
        break;
      }
      case '!':
        out += next === '[' ? '\\!' : '!';
        break;
      case '~':
        out += next === '~' || previous === '~' ? '\\~' : '~';
        break;
      case '=':
        out += next === '=' || previous === '=' ? '\\=' : '=';
        break;
      default:
        out += char;
        break;
    }
  }
  return out;
}

/** Escapes `#`, `-`, `>`, `1.` etc. when they would open a block at line start. */
function escapeLineStarts(text: string): string {
  return text
    .split('\n')
    .map((line) =>
      line
        .replace(/^(\s*)([#>+-])(\s|$)/, '$1\\$2$3')
        .replace(/^(\s*)(\d{1,9})([.)])(\s|$)/, '$1$2\\$3$4')
        .replace(/^(\s*)(={3,}|-{3,}|_{3,})\s*$/, '$1\\$2'),
    )
    .join('\n');
}

/**
 * Flattens a cell's Markdown onto the single line a GFM table allows.
 *
 * A cell can legitimately hold blocks — a list, a callout, several paragraphs —
 * none of which survive a pipe row. Block markers are dropped and the parts are
 * separated with `<br>`, which renders inside a cell, rather than being run
 * together into nonsense like "> [!WARNING] > Do not skip this. - a - b".
 */
function escapeTableCell(markdown: string): string {
  const lines = markdown
    .split('\n')
    .map((line) =>
      line
        // A quoted line, including a GFM alert, reads as plain text in a cell.
        .replace(/^\s*>\s?/, '')
        .replace(/^\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION)\]\s*$/i, (_m, kind) => `${String(kind).toUpperCase()}:`)
        .trimEnd(),
    )
    .filter((line) => line.trim().length > 0);
  return lines.join('<br>').replace(/\|/g, '\\|');
}

const YAML_RESERVED = /^(y|Y|yes|Yes|YES|n|N|no|No|NO|true|True|TRUE|false|False|FALSE|on|On|ON|off|Off|OFF|null|Null|NULL|~)$/;

/**
 * Emits a YAML scalar. Strings are quoted unless they are unambiguously plain,
 * so dates, version numbers and booleans-as-text survive a round trip as strings.
 */
function yamlScalar(value: unknown): string {
  if (value === null || value === undefined) return "''";
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  const str = String(value);
  if (str === '') return "''";
  // A single-quoted YAML scalar cannot carry a newline, so anything multi-line
  // or with control characters is emitted double-quoted with escapes.
  if (/[\n\r\t\u0000-\u001f]/.test(str)) {
    const escaped = str
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"')
      .replace(/\n/g, '\\n')
      .replace(/\r/g, '\\r')
      .replace(/\t/g, '\\t')
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u001f]/g, (c) => `\\x${c.charCodeAt(0).toString(16).padStart(2, '0')}`);
    return `"${escaped}"`;
  }
  const plain =
    /^[A-Za-z][A-Za-z0-9 ._/@+-]*$/.test(str) &&
    !YAML_RESERVED.test(str) &&
    !str.endsWith(' ');
  if (plain) return str;
  return `'${str.replace(/'/g, "''")}'`;
}

/**
 * Keys that carry governance meaning. A later duplicate would win with most
 * YAML parsers, so a user-supplied key is never allowed to shadow one.
 */
const RESERVED_FRONT_MATTER_KEYS = new Set([
  'id',
  'reference',
  'title',
  'type',
  'version',
  'status',
  'organisation',
  'owner',
  'approver',
  'classification',
  'effective_from',
  'review_due',
  'tags',
]);

/** A key must be a plain identifier; anything else cannot be written safely. */
const SAFE_KEY = /^[A-Za-z][A-Za-z0-9_-]{0,60}$/;

export function renderFrontMatter(meta: DocumentFrontMatter): string {
  const lines: string[] = ['---'];
  const written = new Set<string>();

  for (const [key, value] of Object.entries(meta)) {
    if (value === undefined || value === null || value === '') continue;
    /*
     * Document metadata is author-supplied, so a key such as
     * "classification: public\n---\n# Forged" would otherwise inject a second
     * governance value and close the block early. Only plain identifiers are
     * emitted, and never twice.
     */
    if (!SAFE_KEY.test(key)) continue;
    if (written.has(key)) continue;
    written.add(key);

    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      lines.push(`${key}: [${value.map((v) => yamlScalar(v)).join(', ')}]`);
    } else if (typeof value === 'object') {
      const entries = Object.entries(value as Record<string, unknown>).filter(([k]) => SAFE_KEY.test(k));
      if (!entries.length) continue;
      lines.push(`${key}:`);
      for (const [k, v] of entries) lines.push(`  ${k}: ${yamlScalar(v)}`);
    } else {
      lines.push(`${key}: ${yamlScalar(value)}`);
    }
  }

  lines.push('---');
  return lines.join('\n');
}

function crossReferenceTarget(attrs: CrossReferenceAttrs, style: 'link' | 'text'): string | null {
  if (attrs.url) return attrs.url;
  const anchor = attrs.sectionAnchor ? `#${attrs.sectionAnchor}` : '';
  if (!attrs.documentId && !attrs.documentRef) return anchor || null;
  if (style === 'text') return null;
  const ref = attrs.documentRef || attrs.documentId;
  return `${ref}.md${anchor}`;
}

function renderCrossReference(node: RichNode, options: MarkdownOptions): string {
  const attrs = (node.attrs ?? {}) as unknown as CrossReferenceAttrs;
  const label = attrs.label || attrs.documentRef || 'reference';
  const style = options.crossReferenceStyle ?? 'link';
  const target = crossReferenceTarget(attrs, style);
  if (!target) {
    const ref = attrs.documentRef ? ` (${attrs.documentRef})` : '';
    return `${escapeText(label)}${ref}`;
  }
  return `[${escapeText(label)}](${target})`;
}

function markOrder(mark: Mark): number {
  const order: Record<string, number> = { link: 0, bold: 1, italic: 2, underline: 3, strike: 4, highlight: 5, code: 6 };
  return order[mark.type] ?? 9;
}

function applyMarks(text: string, marks: Mark[] | undefined): string {
  if (!marks?.length) return text;
  const sorted = [...marks].sort((a, b) => markOrder(b) - markOrder(a));
  let out = text;
  for (const mark of sorted) {
    switch (mark.type) {
      case 'bold':
        out = `**${out}**`;
        break;
      case 'italic':
        out = `*${out}*`;
        break;
      case 'strike':
        out = `~~${out}~~`;
        break;
      case 'code': {
        // Inline code is literal, so undo the surrounding escaping, then pick a
        // fence longer than any run of backticks inside the text.
        const literal = out.replace(/\\([\\`*_{}\[\]()#+\-.!|<>~=])/g, '$1');
        const longest = Math.max(0, ...[...literal.matchAll(/`+/g)].map((m) => m[0].length));
        const fence = '`'.repeat(longest + 1);
        const pad = literal.startsWith('`') || literal.endsWith('`') ? ' ' : '';
        out = `${fence}${pad}${literal}${pad}${fence}`;
        break;
      }
      case 'underline':
        out = `<u>${out}</u>`;
        break;
      case 'highlight':
        // `==` is not GFM; <mark> is valid HTML and renders in every viewer.
        out = `<mark>${out}</mark>`;
        break;
      case 'link': {
        // The same restriction as the HTML exporter: an exported document must
        // not carry a link to a scheme or origin the author smuggled in.
        const href = safeMarkdownHref(String((mark.attrs as { href?: string } | undefined)?.href ?? ''));
        if (!href) break;
        const title = (mark.attrs as { title?: string } | undefined)?.title;
        out = `[${out}](${encodeURI(href)}${title ? ` "${title.replace(/"/g, '')}"` : ''})`;
        break;
      }
      default:
        break;
    }
  }
  return out;
}

/** Serialises an array of inline nodes to Markdown. */
export function inlineToMarkdown(nodes: RichNode[] | undefined, options: MarkdownOptions = {}): string {
  if (!nodes?.length) return '';
  let out = '';
  for (const node of nodes) {
    switch (node.type) {
      case 'text':
        out += applyMarks(escapeText(node.text ?? ''), node.marks);
        break;
      case 'hardBreak':
        out += '  \n';
        break;
      case 'crossReference':
        out += applyMarks(renderCrossReference(node, options), node.marks);
        break;
      case 'image': {
        const attrs = (node.attrs ?? {}) as { src?: string; alt?: string; title?: string };
        const src = safeMarkdownHref(String(attrs.src ?? ''));
        if (!src) break;
        out += `![${escapeText(attrs.alt ?? '')}](${encodeURI(src)}${attrs.title ? ` "${attrs.title.replace(/"/g, '')}"` : ''})`;
        break;
      }
      default:
        out += inlineToMarkdown(node.content, options);
        break;
    }
  }
  return out;
}

const CALLOUT_LABELS: Record<string, string> = {
  note: 'NOTE',
  info: 'NOTE',
  tip: 'TIP',
  important: 'IMPORTANT',
  warning: 'WARNING',
  caution: 'CAUTION',
};

function indentBlock(text: string, prefix: string, firstPrefix = prefix): string {
  const lines = text.split('\n');
  return lines
    .map((line, i) => {
      const p = i === 0 ? firstPrefix : prefix;
      return line.length ? p + line : p.trimEnd();
    })
    .join('\n');
}

function renderTable(node: RichNode, options: MarkdownOptions): string {
  const rows = node.content ?? [];
  if (!rows.length) return '';
  const grid: string[][] = [];
  let headerRow = false;
  rows.forEach((row, rowIndex) => {
    const cells = (row.content ?? []).map((cell) => {
      if (rowIndex === 0 && cell.type === 'tableHeader') headerRow = true;
      return escapeTableCell(blocksToMarkdown(cell.content ?? [], options).trim());
    });
    grid.push(cells);
  });
  const width = Math.max(...grid.map((r) => r.length));
  const pad = (cells: string[]) => {
    const copy = [...cells];
    while (copy.length < width) copy.push('');
    return copy;
  };
  const lines: string[] = [];
  const first = pad(grid[0] ?? []);
  if (headerRow) {
    lines.push(`| ${first.join(' | ')} |`);
    lines.push(`| ${first.map(() => '---').join(' | ')} |`);
    for (const row of grid.slice(1)) lines.push(`| ${pad(row).join(' | ')} |`);
  } else {
    // GFM has no header-less table. Promoting the first row keeps the Markdown
    // valid and round-trips to the same visible grid.
    lines.push(`| ${first.join(' | ')} |`);
    lines.push(`| ${first.map(() => '---').join(' | ')} |`);
    for (const row of grid.slice(1)) lines.push(`| ${pad(row).join(' | ')} |`);
  }
  return lines.join('\n');
}

function renderList(node: RichNode, options: MarkdownOptions, ordered: boolean): string {
  const start = Number((node.attrs as { start?: number } | undefined)?.start ?? 1);
  const items = node.content ?? [];
  const out: string[] = [];
  items.forEach((item, index) => {
    let bullet: string;
    if (ordered) bullet = `${start + index}. `;
    else if (item.type === 'taskItem') bullet = (item.attrs as { checked?: boolean } | undefined)?.checked ? '- [x] ' : '- [ ] ';
    else bullet = '- ';
    const inner = blocksToMarkdown(item.content ?? [], options);
    out.push(indentBlock(inner, ' '.repeat(bullet.length), bullet));
  });
  return out.join('\n');
}

/** Serialises an array of block nodes to Markdown. */
export function blocksToMarkdown(nodes: RichNode[] | undefined, options: MarkdownOptions = {}): string {
  if (!nodes?.length) return '';
  const blocks: string[] = [];
  for (const node of nodes) {
    switch (node.type) {
      case 'paragraph': {
        const text = escapeLineStarts(inlineToMarkdown(node.content, options));
        blocks.push(text);
        break;
      }
      case 'heading': {
        const level = Math.min(6, Math.max(1, Number((node.attrs as { level?: number } | undefined)?.level ?? 3)));
        blocks.push(`${'#'.repeat(level)} ${inlineToMarkdown(node.content, options)}`);
        break;
      }
      case 'bulletList':
        blocks.push(renderList(node, options, false));
        break;
      case 'taskList':
        blocks.push(renderList(node, options, false));
        break;
      case 'orderedList':
        blocks.push(renderList(node, options, true));
        break;
      case 'blockquote':
        blocks.push(indentBlock(blocksToMarkdown(node.content, options), '> '));
        break;
      case 'callout': {
        const kind = String((node.attrs as { kind?: string } | undefined)?.kind ?? 'note').toLowerCase();
        const label = CALLOUT_LABELS[kind] ?? 'NOTE';
        const inner = blocksToMarkdown(node.content, options);
        blocks.push(`> [!${label}]\n${indentBlock(inner, '> ')}`);
        break;
      }
      case 'codeBlock': {
        const lang = String((node.attrs as { language?: string } | undefined)?.language ?? '');
        const code = (node.content ?? []).map((c) => c.text ?? '').join('');
        // The fence must be longer than any backtick run inside the block.
        const longest = Math.max(2, ...[...code.matchAll(/^`{3,}/gm)].map((m) => m[0].length));
        const fence = '`'.repeat(longest + 1);
        blocks.push(`${fence}${lang}\n${code}\n${fence}`);
        break;
      }
      case 'horizontalRule':
        blocks.push('---');
        break;
      case 'table':
        blocks.push(renderTable(node, options));
        break;
      case 'doc':
        blocks.push(blocksToMarkdown(node.content, options));
        break;
      default: {
        const inner = node.content?.length ? blocksToMarkdown(node.content, options) : inlineToMarkdown(node.content, options);
        if (inner) blocks.push(inner);
        break;
      }
    }
  }
  return blocks.filter((b) => b.trim().length > 0).join('\n\n');
}

/** Serialises a whole document (front matter, title, TOC, sections) to Markdown. */
export function contentToMarkdown(content: DocumentContent, options: MarkdownOptions = {}): string {
  const {
    frontMatter = null,
    includeTitle = true,
    title,
    numberHeadings = true,
    includeToc = false,
    skipInternal = true,
    baseHeadingLevel = 2,
  } = options;

  const parts: string[] = [];
  if (frontMatter) parts.push(renderFrontMatter(frontMatter));
  const docTitle = title ?? frontMatter?.title;
  if (includeTitle && docTitle) parts.push(`# ${escapeText(docTitle)}`);

  const flat = flattenSections(content, { skipInternal });

  if (includeToc && flat.length) {
    const toc = flat
      .map((entry) => {
        const indent = '  '.repeat(Math.max(0, entry.depth - 1));
        const title = escapeText(entry.section.title);
        const label = entry.number && numberHeadings ? `${entry.number} ${title}` : title;
        return `${indent}- [${label}](#${entry.section.anchor})`;
      })
      .join('\n');
    parts.push(`## Contents\n\n${toc}`);
  }

  for (const entry of flat) {
    const level = Math.min(6, baseHeadingLevel + entry.depth - 1);
    const prefix = numberHeadings && entry.number ? `${entry.number} ` : '';
    parts.push(`${'#'.repeat(level)} ${prefix}${escapeText(entry.section.title)}`.trimEnd());
    const body = blocksToMarkdown(entry.section.body.content, options);
    if (body) parts.push(body);
  }

  return `${parts.filter(Boolean).join('\n\n').trim()}\n`;
}
